import React, { useState, useEffect } from 'react';
import AdminSessionManager from '../../components/AdminSessionManager';
import ContentTypeSelector from './components/ContentTypeSelector';
import DailyMessageEditor from './components/DailyMessageEditor';
import MiniTaskEditor from './components/MiniTaskEditor';
import PollEditor from './components/PollEditor';
import HumorEditor from './components/HumorEditor';
import GiveawayEditor from './components/GiveawayEditor';
import SocialAwarenessEditor from './components/SocialAwarenessEditor';
import SurpriseEditor from './components/SurpriseEditor';
import ContentPreview from './components/ContentPreview';
import ParticipantList from './components/ParticipantList';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { saveContent } from '../../services/contentService';

const AdminPanel = () => {
  const [selectedContentType, setSelectedContentType] = useState('');
  const [contentData, setContentData] = useState({});
  const [activeTab, setActiveTab] = useState('content');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleContentTypeChange = (type) => {
    setSelectedContentType(type);
    setContentData({});
  };

  const handleContentChange = (data) => {
    setContentData(data);
  };

  const handleSaveContent = async () => {
    try {
      setIsSaving(true);

      // Convert kebab-case to camelCase (e.g., 'daily-message' -> 'dailyMessage')
      const toCamelCase = (str) => {
        return str?.replace(/-([a-z])/g, (match, letter) => letter?.toUpperCase());
      };

      const contentToSave = {
        ...contentData,
        contentType: toCamelCase(selectedContentType),
        scheduledDate: contentData?.scheduledDate || new Date()?.toISOString()?.split('T')?.[0],
        isActive: contentData?.isActive !== undefined ? contentData?.isActive : true
      };

      const { data, error } = await saveContent(contentToSave);
      
      if (error) {
        console.error('Error saving content:', error);
        alert('İçerik kaydedilirken bir hata oluştu');
      } else {
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
        setSelectedContentType('');
        setContentData({});
      }
    } catch (error) {
      console.error('Error saving content:', error);
      alert('İçerik kaydedilirken bir hata oluştu');
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetDaily = () => {
    if (window.confirm('Günlük verileri sıfırlamak istediğinizden emin misiniz?')) {
      console.log('Resetting daily data');
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    }
  };

  const renderContentEditor = () => {
    switch (selectedContentType) {
      case 'daily-message':
        return <DailyMessageEditor content={contentData} onChange={handleContentChange} />;
      case 'mini-task':
        return <MiniTaskEditor content={contentData} onChange={handleContentChange} />;
      case 'poll':
        return <PollEditor content={contentData} onChange={handleContentChange} />;
      case 'humor':
        return <HumorEditor content={contentData} onChange={handleContentChange} />;
      case 'giveaway':
        return <GiveawayEditor content={contentData} onChange={handleContentChange} />;
      case 'social-awareness':
        return <SocialAwarenessEditor content={contentData} onChange={handleContentChange} />;
      case 'surprise':
        return <SurpriseEditor content={contentData} onChange={handleContentChange} />;
      default:
        return (
          <div className="card text-center py-12">
            <Icon name="FileQuestion" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
            <p className="text-muted-foreground">
              İçerik düzenlemek için yukarıdan bir içerik tipi seçin
            </p>
          </div>
        );
    }
  };

  const tabs = [
    { id: 'content', label: 'İçerik Yönetimi', icon: 'FileEdit' },
    { id: 'analytics', label: 'Analitik', icon: 'BarChart3' },
    { id: 'participants', label: 'Katılımcılar', icon: 'Users' }
  ];

  return (
    <AdminSessionManager correctPassword="admin123">
      <div className="min-h-screen bg-background">
        {showSuccessMessage && (
          <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top">
            <div className="card bg-success text-success-foreground shadow-xl">
              <div className="flex items-center gap-3">
                <Icon name="CheckCircle" size={24} />
                <p className="font-medium">İşlem başarıyla tamamlandı!</p>
              </div>
            </div>
          </div>
        )}

        <div className="container-safe py-6 md:py-8">
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
                  İçerik Yönetim Paneli
                </h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  Günlük içerikleri yönetin ve kampanya performansını takip edin
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  size="default"
                  onClick={handleResetDaily}
                  iconName="RotateCcw"
                  iconPosition="left"
                >
                  Günlük Sıfırla
                </Button>
              </div>
            </div>
          </div>

          <div className="mb-6 md:mb-8">
            <div className="flex gap-2 overflow-x-auto pb-2">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 md:px-6 py-3 rounded-lg font-medium transition-all duration-250 whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'bg-primary text-primary-foreground shadow-md'
                      : 'bg-card text-foreground hover:bg-muted border border-border'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  <span className="text-sm md:text-base">{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {activeTab === 'content' && (
            <div className="space-y-6 md:space-y-8">
              <div className="card">
                <ContentTypeSelector
                  selectedType={selectedContentType}
                  onTypeChange={handleContentTypeChange}
                />
              </div>

              {selectedContentType && (
                <>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                    <div className="card">
                      {renderContentEditor()}
                    </div>

                    <ContentPreview
                      contentType={selectedContentType}
                      content={contentData}
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button
                      variant="default"
                      size="lg"
                      onClick={handleSaveContent}
                      iconName="Save"
                      iconPosition="left"
                      fullWidth
                      loading={isSaving}
                      disabled={isSaving}
                    >
                      {isSaving ? 'Kaydediliyor...' : 'İçeriği Kaydet ve Yayınla'}
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={() => {
                        setSelectedContentType('');
                        setContentData({});
                      }}
                      iconName="X"
                      iconPosition="left"
                      className="sm:w-auto"
                    >
                      İptal
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'analytics' && <AnalyticsDashboard />}

          {activeTab === 'participants' && <ParticipantList />}
        </div>
      </div>
    </AdminSessionManager>
  );
};

export default AdminPanel;